// ./src/components/features/EventTickets/TicketGenerateDrawer/TicketGenerateDrawer.tsx
//
// Generate a batch of blank inventory tickets of one type — pre-printed and sold
// in person later. Each ticket gets a unique code + the type's price snapshot and
// starts in "generated" state; a later mark-as-sold (bulk update / scan) moves
// them to sold. No capacity check — this is physical inventory, not a sale.
//
// ROADMAP #120 (2026-10-08): this was a modal opened from `useState`; it is now a drawer (house rule: AppDrawer, not a
// modal) on its own URL stage (`…/generate`), so a reload or a phone resumed from the background reopens it. The
// options start from their defaults each time it opens.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Text, NumberInput, Loader, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { TicketType, TicketTypeService, listAllPages } from "@/utils/api";
import { AppDrawer, SelectField } from "@/components/generic";
import { formatErrors, getFormErrors } from "@/utils";
import { useEventTimeSlots, useFormSnapshot, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { bookableSlotOptions } from "@/utils/eventDates";

const GENERATE_MAX = 500;
const DEFAULT_QUANTITY = 10;

// A refused date is keyed `eventTimeSlotId` by the server — it renders on the date picker.
const KNOWN_FORM_FIELDS = ["eventTimeSlotId"] as const;

export interface TicketGenerateDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** Runs the generate; resolves with the OpResult so the drawer can surface errors. */
  onConfirm: (ticketTypeId: string, quantity: number, eventTimeSlotId?: string | null) => Promise<OpResult>;
  inProgress?: boolean;
  /** ROADMAP A4 — a recurring event: every printed ticket admits to one date, so one must be chosen. */
  recurring?: boolean;
  timezone?: string | null;
}

const fmtPrice = (tt: TicketType): string => {
  if (tt.price == null) {
    return "";
  }

  const n = Number(tt.price);
  if (!Number.isFinite(n)) {
    return "";
  }

  const v = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return tt.currencyCode ? ` · ${tt.currencyCode} ${v}` : ` · ${v}`;
};

const TicketGenerateDrawerComponent: React.FC<TicketGenerateDrawerProps> = ({
  opened,
  onClose,
  eventId,
  onConfirm,
  inProgress = false,
  recurring = false,
  timezone = null,
}) => {
  const { t } = useTranslation(["ticketSales", "common"]);

  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<TicketType[]>([]);
  const [ticketTypeId, setTicketTypeId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | "">(DEFAULT_QUANTITY);
  const [error, setError] = useState<OpResult | null>(null);
  const [dateId, setDateId] = useState<string | null>(null);
  const { slots } = useEventTimeSlots(opened && recurring ? eventId : null);
  const dateOptions = useMemo(() => bookableSlotOptions(slots, timezone), [slots, timezone]);

  // Esc / Cancel ask before discarding changed options (#123). A lone type is picked FOR the host once the types
  // arrive — that is the form's starting point, not a change, so the snapshot is taken after it.
  const { isDirty, seedFrom, clear } = useFormSnapshot({ ticketTypeId, quantity, dateId });

  useEffect(() => {
    if (!opened) {
      clear();

      return undefined;
    }

    setError(null);
    setTicketTypeId(null);
    setDateId(null);
    setQuantity(DEFAULT_QUANTITY);
    setLoading(true);

    let cancelled = false;

    listAllPages(TicketTypeService.list, { eventId: [eventId], isAddOn: false }).then((res) => {
      if (cancelled) {
        return;
      }

      let startTypeId: string | null = null;

      if (res.code === OP_RESULT_CODES.OK && Array.isArray(res.data)) {
        const list = res.data as TicketType[];
        setTypes(list);
        if (list.length === 1 && list[0].id) {
          startTypeId = list[0].id;
        }
      } else {
        setError(res);
      }

      setTicketTypeId(startTypeId);
      seedFrom({ ticketTypeId: startTypeId, quantity: DEFAULT_QUANTITY, dateId: null });
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [opened, eventId, clear, seedFrom]);

  const options = useMemo(
    () =>
      types
        .filter((tt) => tt.id)
        .map((tt) => ({ value: tt.id as string, label: `${tt.name || "—"}${fmtPrice(tt)}` })),
    [types]
  );

  const qty = quantity === "" ? 0 : Number(quantity);
  // A type the host restricted to one date already names it; any other type needs a date picked.
  const impliedDateId = types.find((tt) => tt.id === ticketTypeId)?.eventTimeSlotId ?? null;
  const needsDate = recurring && !impliedDateId;
  const canSubmit = !!ticketTypeId && qty >= 1 && qty <= GENERATE_MAX && (!needsDate || !!dateId);
  const canConfirm = !loading && canSubmit && !inProgress;

  const handleConfirm = useCallback(async () => {
    if (!canConfirm || !ticketTypeId) {
      return;
    }

    setError(null);

    const result = await onConfirm(ticketTypeId, qty, recurring ? dateId ?? impliedDateId : null);

    if (result.code === OP_RESULT_CODES.OK) {
      onClose();

      return;
    }

    setError(result);
  }, [canConfirm, ticketTypeId, qty, onConfirm, onClose, recurring, dateId, impliedDateId]);

  // ⌘S / ⌘Enter only. Esc is AppDrawer's own — it honours `closable` (not while generating).
  useModalShortcuts({ onConfirm: handleConfirm, canConfirm, enabled: opened });

  const { fields: fieldErrors, genericErrors } = useMemo(() => getFormErrors(error, KNOWN_FORM_FIELDS), [error]);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("ticketSales:generate.title", { defaultValue: "Generate tickets" })}
      position="right"
      size="md"
      mobileFitContent
      closable={!inProgress}
      loading={inProgress}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="confirm-cancel"
      onConfirm={handleConfirm}
      confirmLabel={t("ticketSales:generate.confirm", { defaultValue: "Generate {{n}}", n: qty || 0 })}
      confirmIcon="Ticket"
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={!canConfirm}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <Text size="md" c="dimmed">
          {t("ticketSales:generate.hint", {
            defaultValue: "Pre-print a batch of blank tickets to sell in person. They start unassigned — mark them sold once handed over.",
          })}
        </Text>

        {loading ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <>
            <SelectField
              label={t("ticketSales:generate.type", { defaultValue: "Ticket type" })}
              placeholder={t("ticketSales:generate.pickType", { defaultValue: "Pick a type" })}
              data={options}
              value={ticketTypeId}
              onChange={(_name, v) => setTicketTypeId(v)}
              searchable
              size="md"
              nothingFoundMessage={t("ticketSales:generate.noTypes", { defaultValue: "No ticket types" })}
              // A type restricted to one date sends that date: a refusal of it belongs to the type when no date picker shows.
              error={needsDate ? undefined : fieldErrors.eventTimeSlotId}
            />
            {needsDate && (
              <SelectField
                label={t("ticketSales:generate.date", { defaultValue: "Date" })}
                placeholder={t("ticketSales:generate.pickDate", { defaultValue: "Which date do these tickets admit to?" })}
                data={dateOptions}
                value={dateId}
                onChange={(_name, v) => setDateId(v)}
                searchable
                size="md"
                error={fieldErrors.eventTimeSlotId}
                nothingFoundMessage={t("ticketSales:sale.noDates", { defaultValue: "No upcoming dates" })}
              />
            )}
            <NumberInput
              label={t("ticketSales:generate.quantity", { defaultValue: "How many" })}
              value={quantity}
              onChange={(v) => setQuantity(v === "" ? "" : Number(v))}
              min={1}
              max={GENERATE_MAX}
              clampBehavior="strict"
              allowDecimal={false}
              size="md"
            />
          </>
        )}
      </Stack>
    </AppDrawer>
  );
};

export const TicketGenerateDrawer = React.memo(TicketGenerateDrawerComponent);
